import React, { useRef, useCallback } from 'react';
import { TokenIcon } from './TokenIcon';
import { CHAIN_KEYS } from '../chains/interfaces';
import { getChainConfig } from '../chains/registry';

/**
 * TokenSelector - Radio-pill group for choosing the stablecoin to pay with.
 * Follows the WAI-ARIA radio group pattern: one tab stop for the group,
 * arrow keys move and select within it.
 *
 * @param {Object} props
 * @param {string} props.selectedStablecoin - Currently selected stablecoin key
 * @param {Function} props.onSelect - Callback when a new stablecoin is selected
 * @param {string} props.balance - Formatted balance of the selected stablecoin
 * @param {string} props.balanceLabel - Label that scopes the balance to the selected chain
 * @param {boolean} props.isConnected - Whether wallet is connected
 * @param {boolean} props.isLoading - Whether balance is loading
 */
export const TokenSelector = ({
  selectedStablecoin,
  onSelect,
  stablecoins = getChainConfig(CHAIN_KEYS.ETHEREUM).stablecoins,
  balance,
  balanceLabel = 'Balance',
  isConnected,
  isLoading,
}) => {
  const groupRef = useRef(null);
  const tokenKeys = Object.keys(stablecoins || {});
  const tabStopKey = tokenKeys.includes(selectedStablecoin) ? selectedStablecoin : tokenKeys[0];

  const handleKeyDown = useCallback(
    event => {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step || tokenKeys.length === 0) return;
      event.preventDefault();
      const currentIndex = Math.max(tokenKeys.indexOf(selectedStablecoin), 0);
      const nextIndex = (currentIndex + step + tokenKeys.length) % tokenKeys.length;
      onSelect(tokenKeys[nextIndex]);
      groupRef.current?.querySelectorAll('[role="radio"]')[nextIndex]?.focus();
    },
    [tokenKeys, selectedStablecoin, onSelect]
  );

  return (
    <div className="token-from">
      <div className="token-label" id="token-pay-label">
        You pay
      </div>
      <div
        ref={groupRef}
        className="token-pills"
        role="radiogroup"
        aria-labelledby="token-pay-label"
        onKeyDown={handleKeyDown}
      >
        {tokenKeys.map(key => {
          const isSelected = key === selectedStablecoin;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={key === tabStopKey ? 0 : -1}
              className={`token-pill ${isSelected ? 'selected' : ''}`}
              onClick={() => onSelect(key)}
            >
              <TokenIcon symbol={key} size={30} className="token-pill-icon" />
              <span className="token-pill-symbol">{key}</span>
            </button>
          );
        })}
      </div>
      {isConnected && (
        <div className="balance">
          {balanceLabel}: {isLoading ? <span className="loading-text">Loading...</span> : balance}
        </div>
      )}
    </div>
  );
};
